import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { toast } from 'sonner'
import {
  Eye, Download,
  Pencil, HeartPulse, Shield, Phone, Trash2, Calendar, ClipboardPlus,
  FileText, Receipt, Wallet, ArrowRight, MessageSquare, Users, Plus, Scan, Grid3x3,
  AlertTriangle, GitMerge, UserCheck, ClipboardCheck, ChevronDown, ChevronRight, StickyNote,
  Sparkles, FolderOpen, Pill, Send, Upload, Printer, Clock,
} from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'
import Odontogram from '../components/Odontogram'
import PaymentModal from '../components/PaymentModal'
// Shared with the Digital file page — one component for a signed form, so the two places
// reception can act on a signature cannot drift apart.
import SignedPackCard from '../components/SignedPackCard'
import PatientFormModal from '../components/PatientFormModal'
import AppointmentFormModal from '../components/AppointmentFormModal'
import DicomViewer from '../components/DicomViewer'
import PatientSearchSelect from '../components/PatientSearchSelect'

const STATUS_STYLES = {
  scheduled:   'bg-amber-100 text-amber-800',
  confirmed:   'bg-emerald-100 text-emerald-800',
  completed:   'bg-blue-100 text-blue-800',
  cancelled:   'bg-red-100 text-red-800',
  no_show:     'bg-gray-100 text-gray-600',
  rescheduled: 'bg-purple-100 text-purple-800',
  // document statuses
  planned:     'bg-amber-100 text-amber-800',
  active:      'bg-blue-100 text-blue-800',
  draft:       'bg-gray-100 text-gray-600',
  sent:        'bg-amber-100 text-amber-800',
  accepted:    'bg-emerald-100 text-emerald-800',
  open:        'bg-amber-100 text-amber-800',
  part_paid:   'bg-orange-100 text-orange-800',
  paid:        'bg-emerald-100 text-emerald-800',
  void:        'bg-gray-100 text-gray-500',
}

const ZAR = (n) => `R${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const dateZA = (s, opts) => s ? new Date(s).toLocaleDateString('en-ZA', opts) : '—'

// The EXACT prop names the live-refresh re-syncs (the money cards + Estimates/Invoices/Account/plan
// lists that change after a payment or an estimate edit). Every key here MUST be a prop emitted by
// PatientsController#show (~patients_controller.rb:196-245) — the live-refresh audit parses this list
// and proves it stays a SUBSET of the rendered props, so a wrong/renamed key can never silently turn
// the partial reload into a FULL reload (a re-mount) — which is exactly what let the previous naïve
// version LOOP. This is a genuine partial reload of a bounded set, nothing more.
const LIVE_REFRESH_KEYS = [
  'estimates', 'invoices', 'open_estimates', 'open_invoices',
  'outstanding_balance', 'account_summary', 'courses_of_treatment',
  'billing_account', 'treatment_journeys', 'appointments', 'conversations',
]

export default function PatientShow({
  referral_sources,
  patient, medical_history, scheme = null, schemes = [], appointments, conversations, write_sessions = [], charted_visits = [],
  courses_of_treatment: courses = [],
  estimates = [],
  treatment_journeys: journeys = [],
  case_templates: templates = [],
  invoices = [],
  imaging_studies: imaging = [],
  documents = [],
  billing_account: billingAccount = null,
  account_summary: accountSummary = {},
  open_courses_of_treatment: openCourses = [],
  open_estimates: openEstimates = [],
  open_invoices: openInvoices = [],
  outstanding_balance: outstandingBalance = 0,
  google_review_url: googleReviewUrl = null,
  next_appointment: nextAppointment = null,
  tooth_chart: toothChart = {},
  tooth_chart_detail: toothChartDetail = {},
  likely_match: likelyMatch = null,
  intake_review: intakeReview = null,
  notepad_notes: notes = [],
  communications = [],
  signed_pack: signedPack = null,   // the signed intake pack as ONE card (SignedIntakePack)
  intake_in_progress: intakeInProgress = null, // a STARTED but unsubmitted intake — printable as-is
  providers = [],   // dentists for the Book-appointment modal (Paul 2026-09-02: no list = no Dentist field)
}) {
  // Dentists don't handle billing — hide the Invoices + Account tabs (server also blocks the
  // /invoices + /accounts routes; see enforce_dentist_scope!). Estimates stay (clinical plan).
  const isDentist = usePage().props?.current_user?.role === 'dentist'
  const [editOpen, setEditOpen] = useState(false)
  const [bookOpen, setBookOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  // Land on a specific tab when redirected with a #hash (e.g. after "Draft estimate from latest scan"
  // redirects to /patients/:id#estimates so the freshly-drafted estimate is in view). Safe allowlist.
  const [journeySel, setJourneySel] = useState([])
  const toggleJourney = (id) => setJourneySel((v) => v.includes(id) ? v.filter((x) => x !== id) : [...v, id])
  const buildJourney = () => { if (journeySel.length) router.post('/journeys', { patient_id: patient.id, estimate_ids: journeySel }, { preserveScroll: true, onSuccess: () => setJourneySel([]) }) }
  const sendJourney = (id) => { if (window.confirm('Send this treatment journey to the patient on WhatsApp?')) router.post(`/journeys/${id}/send_whatsapp`, {}, { preserveScroll: true, onSuccess: (page) => { const al = page?.props?.flash?.alert; al ? toast.error(al) : toast.success(page?.props?.flash?.notice || 'Treatment journey sent on WhatsApp') } }) }
  const emailJourney = (id) => {
    // has_email works even when the address is redacted for a dentist (patient.email is nil then); the
    // send is server-side, so it still reaches the address on file without showing it here.
    if (!(patient.has_email ?? patient.email)) { window.alert(`${patient.full_name} has no email address on file — add one on their profile first, or send on WhatsApp.`); return }
    if (window.confirm(`Email this treatment plan (PDF + online link) to ${patient.full_name}${patient.email ? ` at ${patient.email}` : ''}?`)) router.post(`/journeys/${id}/send_email`, {}, { preserveScroll: true })
  }
  const removeJourney = (id) => { if (window.confirm('Remove this treatment journey?')) router.delete(`/journeys/${id}`, { preserveScroll: true }) }
  const [tab, setTab] = useState(() => {
    try { return window.location.hash === '#estimates' ? 'estimates' : 'overview' } catch { return 'overview' }
  })
  const [dicomStudy, setDicomStudy] = useState(null)
  // ⚡ "Draft estimate from latest scan" — reads the patient's most recent uploaded chart scan on demand.
  const [draftingScan, setDraftingScan] = useState(false)
  const latestScan = [...(documents || [])]
    .filter((d) => d.folder === 'chart_scans' && d.url)
    .sort((a, b) => new Date(b.captured_at || 0) - new Date(a.captured_at || 0))[0] || null
  const draftFromScan = () => {
    if (draftingScan) return
    router.post(`/patients/${patient.id}/estimates/from_scan`, {}, {
      preserveScroll: true,
      onStart: () => setDraftingScan(true),
      onFinish: () => setDraftingScan(false),
      onSuccess: (page) => {
        const al = page?.props?.flash?.alert
        al ? toast.error(al) : toast.success(page?.props?.flash?.notice || 'Scan read.')
      },
      onError: () => toast.error('Could not read the scan — please try again.'),
    })
  }
  // ⚡ "Draft from latest written chart" — renders the newest hand-written WRITE-mode chart to an image
  // server-side, then reads it through the same pipeline as a scan.
  const [draftingWritten, setDraftingWritten] = useState(false)
  const latestWritten = [...(write_sessions || [])]
    .filter((s) => ((s.chart_ink || 0) + (s.worksheet_ink || 0)) > 0)
    .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))[0] || null
  const draftFromWritten = () => {
    if (draftingWritten) return
    router.post(`/patients/${patient.id}/estimates/from_written_chart`, {}, {
      preserveScroll: true,
      onStart: () => setDraftingWritten(true),
      onFinish: () => setDraftingWritten(false),
      onSuccess: (page) => {
        const al = page?.props?.flash?.alert
        al ? toast.error(al) : toast.success(page?.props?.flash?.notice || 'Chart read.')
      },
      onError: () => toast.error('Could not read the written chart — please try again.'),
    })
  }
  const [reviewOpen, setReviewOpen] = useState(true)
  const [mergeOpen, setMergeOpen] = useState(false)
  const [mergeTarget, setMergeTarget] = useState(null)
  // Right-click estimate → Cancel / Activate / Convert to invoice (Paul 2026-07-01)
  const [estMenu, setEstMenu] = useState(null) // { x, y, est }
  const estAction = (path, est, msg, confirmMsg) => {
    if (confirmMsg && !confirm(confirmMsg)) { setEstMenu(null); return }
    router.post(`/estimates/${est.id}/${path}`, {}, {
      preserveScroll: true,
      onSuccess: (page) => { const al = page?.props?.flash?.alert; al ? toast.error(al) : toast.success(page?.props?.flash?.notice || msg) },
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Action failed'),
    })
    setEstMenu(null)
  }
  // General merge: combine THIS record into any other (the other is kept). Uses the same safe
  // PatientMerge engine as the review queue — appointments, forms, history + account all move over.
  const doMerge = () => {
    if (!mergeTarget) return
    const name = mergeTarget.name || mergeTarget.full_name
    if (!window.confirm(`Merge ${patient.full_name} INTO ${name}? All of ${patient.full_name}'s appointments, forms, history and account move onto ${name}, and the ${patient.full_name} record is removed. This cannot be undone.`)) return
    router.post(`/patients/${patient.id}/merge_into`, { target_id: mergeTarget.id })
  }

  const handleDelete = () => {
    router.delete(`/patients/${patient.id}`, { onSuccess: () => setConfirmDelete(false) })
  }
  const mergeInto = () => {
    if (!likelyMatch) return
    const acct = likelyMatch.account_code ? ` (${likelyMatch.account_code})` : ''
    if (!window.confirm(`Merge this self-registration INTO ${likelyMatch.name}${acct}? Their submitted form, medical history and details move onto that patient and this placeholder is removed.`)) return
    router.post(`/patients/${patient.id}/merge_into`, { target_id: likelyMatch.id })
  }
  const confirmNew = () => {
    if (!window.confirm('Confirm this is a NEW patient (not a duplicate)? They will get an account number and consent on file.')) return
    router.post(`/patients/${patient.id}/confirm_new`)
  }
  const linkAsDependant = () => {
    if (!likelyMatch) return
    const acct = likelyMatch.account_code ? ` (${likelyMatch.account_code})` : ''
    if (!window.confirm(`Link this patient as a DEPENDANT under ${likelyMatch.name}'s account${acct}? They stay a SEPARATE patient, but their billing rolls up to that family account. Their submitted form is filled in automatically — no new account is created.`)) return
    router.post(`/patients/${patient.id}/link_as_dependant`, { target_id: likelyMatch.id })
  }
  const approveAndPromote = () => {
    if (!window.confirm("Approve this self-registration and write ALL the captured form data into the patient record? This fills the structured fields from what the patient submitted (you won't need to re-type).")) return
    router.post(`/patients/${patient.id}/promote`)
  }
  const reactivatePatient = () => {
    if (!window.confirm(`Reactivate ${patient.full_name}? This clears the deceased flag and turns automated messaging (birthday, recalls, reminders, nudges) back ON. Use this only if the death was reported in error.`)) return
    router.post(`/patients/${patient.id}/reactivate`, {}, { preserveScroll: true })
  }
  const markDeceased = () => {
    if (!window.confirm(`Mark ${patient.full_name} as DECEASED?\n\nThis permanently stops ALL automated messaging to this patient — birthday wishes, recall/check-up reminders, appointment reminders, feedback & review requests, and every nudge. Use this when you've been told the patient has passed away.\n\nIt's reversible: an "Undo" appears on the badge if it was a mistake.`)) return
    router.post(`/patients/${patient.id}/mark_deceased`, {}, { preserveScroll: true })
  }
  // 🚨 NOT "mark deceased" (Paul 2026-09-17). A patient who asks to be taken off the list must be
  // silenced just as firmly, but recorded as what they are: someone who does not want WhatsApp from
  // us. Seven patients asked this morning and reception had no button but the bereavement one.
  const stopWhatsapp = () => {
    const reason = window.prompt(
      `Stop all automated WhatsApp to ${patient.full_name}?

This turns OFF recalls, reminders, birthday wishes, follow-ups and every nudge — the same silence as the deceased flag, but WITHOUT recording them as deceased. Reversible.

Why? (optional — e.g. "asked to be removed", "moved away", "wrong number")`,
      ""
    )
    if (reason === null) return
    router.post(`/patients/${patient.id}/stop_whatsapp`, { reason }, { preserveScroll: true })
  }
  const resumeWhatsapp = () => {
    if (!window.confirm(`Turn automated WhatsApp back ON for ${patient.full_name}?`)) return
    router.post(`/patients/${patient.id}/resume_whatsapp`, {}, { preserveScroll: true })
  }
  const minimalPatientList = [{ id: patient.id, name: patient.full_name, phone: patient.phone }]

  // ── LIVE-REFRESH (loop-proof by construction) ─────────────────────────────────────────────────
  // After a payment (recorded on a separate invoice/account page) or an estimate edit, the money
  // cards + Estimates/Invoices/Account/plan lists here would otherwise show STALE, pre-mutation data
  // when the user returns (Inertia serves the patient page from its client-side history cache).
  //
  // The mutation pages set a self-describing localStorage flag `patient_stale:<id>`. This page, when
  // it regains focus, consumes that flag with ONE silent partial reload. The reload is GATED on the
  // flag, and the flag is CLEARED BEFORE the reload fires — so mount / re-render / re-focus /
  // navigation can call the handler arbitrarily many times, yet the reload runs AT MOST ONCE per real
  // mutation. The reload targets only same-URL props (Inertia treats it as a `replace`, which does NOT
  // emit `navigate`) and never re-sets the flag, so a feedback loop is STRUCTURALLY IMPOSSIBLE. (A
  // prior naïve router.reload() on mount+focus LOOPED and took the app down — this flag-gate is the
  // fix; the deps below are bounded to [] / [patient.id] so no reloaded prop can re-trigger it.)
  const patientId = patient.id
  const silentReload = useCallback(() => {
    // Partial reload of ONLY the money/list props (a subset of #show's props ⇒ stays a TRUE partial
    // reload, never a full re-mount), with the progress bar suppressed for this visit (no visible
    // flash). suppress()/release() are paired with onStart/onFinish (see entrypoints/inertia.jsx).
    router.reload({
      only: LIVE_REFRESH_KEYS,
      preserveState: true,
      preserveScroll: true,
      onStart: () => window.__inertiaProgress?.suppress(),
      onFinish: () => window.__inertiaProgress?.release(),
    })
  }, [])
  const consumeStaleFlag = useCallback(() => {
    // Flag-gated + self-clearing: read the flag, and if present REMOVE IT FIRST, then reload. Clearing
    // before the reload (never after) is the whole loop-proofing — a re-entrant call finds no flag.
    let flagged = false
    try {
      const key = 'patient_stale:' + patientId
      flagged = localStorage.getItem(key) === '1'
      if (flagged) localStorage.removeItem(key)
    } catch (e) { flagged = false }
    if (flagged) silentReload()
  }, [patientId, silentReload])
  // (a) Once on mount, ref-guarded, EMPTY deps — the in-app "back to the patient page" case.
  const didConsumeOnMount = useRef(false)
  useEffect(() => {
    if (didConsumeOnMount.current) return
    didConsumeOnMount.current = true
    consumeStaleFlag()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  // (b) On returning to this tab (Alt-Tab back) and on Inertia landing back here — both debounced onto
  // one animation frame and each independently flag-gated. Listeners registered once per patient and
  // cleaned up on unmount. Deps are ONLY the stable consumeStaleFlag (→ patientId), never a reloaded
  // prop, so the reload cannot re-trigger this effect.
  useEffect(() => {
    let raf = 0
    const schedule = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(consumeStaleFlag) }
    const onVisible = () => { if (document.visibilityState === 'visible') schedule() }
    const offNavigate = router.on('navigate', schedule)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelAnimationFrame(raf)
      offNavigate()
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [consumeStaleFlag])

  const TABS = [
    { key: 'overview',   label: 'Overview',        icon: HeartPulse },
    { key: 'treatment',  label: 'Treatment Plans', icon: ClipboardPlus, count: courses.length },
    { key: 'chart',      label: 'Dental Chart',    icon: Grid3x3,       count: Object.keys(toothChart).length },
    { key: 'estimates',  label: 'Estimates',       icon: FileText,      count: estimates.length },
    { key: 'invoices',   label: 'Invoices',        icon: Receipt,       count: invoices.length },
    { key: 'imaging',    label: 'Imaging (SIDEXIS)', icon: Scan,        count: imaging.length },
    { key: 'account',    label: 'Account',         icon: Wallet },
    { key: 'notes',      label: 'Notes',           icon: StickyNote,    count: notes.length },
    { key: 'history',    label: 'Visits & Chats',  icon: Calendar,      count: (appointments?.length || 0) + (communications?.length || 0) },
  ].filter(({ key }) => !(isDentist && (key === 'invoices' || key === 'account')))

  return (
    <DashboardLayout>
      <div className="mb-6">
        <Link href="/patients" className="text-sm text-brand-taupe hover:text-brand-brown transition-colors">
          ← Back to Patients
        </Link>
      </div>

      {patient.needs_review && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="mt-0.5 flex-shrink-0 text-amber-600" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">Self-registered — needs review</p>
              <p className="mt-0.5 text-sm text-amber-800">
                {likelyMatch ? (
                  <>This looks like an existing patient: <strong>{likelyMatch.name}</strong>{likelyMatch.account_code ? ` (${likelyMatch.account_code})` : ''}. Check the submitted form below, then merge — or approve as a new patient.</>
                ) : (
                  <>Check the submitted form below, then approve &amp; promote — every field is written into the record automatically (no re-typing).</>
                )}
              </p>

              {/* Submitted-form review — clean PMS layout of what the patient sent */}
              {intakeReview && (
                <div className="mt-3 rounded-lg border border-amber-200 bg-white">
                  <button onClick={() => setReviewOpen((o) => !o)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-semibold text-amber-900">
                    {reviewOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                    <ClipboardCheck size={15} /> Review submitted form
                  </button>
                  {reviewOpen && (
                    <div className="space-y-4 border-t border-amber-100 px-4 py-3">
                      {/* Identity diff vs the likely existing patient */}
                      {intakeReview.diff?.length > 0 && (
                        <div>
                          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700">Identity vs {likelyMatch ? likelyMatch.name : 'record'}</p>
                          <table className="w-full text-xs">
                            <thead className="text-left text-brand-muted">
                              <tr><th className="py-1 font-medium">Field</th><th className="py-1 font-medium">Submitted</th><th className="py-1 font-medium">On record</th></tr>
                            </thead>
                            <tbody>
                              {intakeReview.diff.map((d) => (
                                <tr key={d.field} className={d.differs ? 'bg-red-50' : ''}>
                                  <td className="py-1 pr-2 text-brand-ink">{d.field}</td>
                                  <td className={`py-1 pr-2 ${d.differs ? 'font-semibold text-red-700' : 'text-brand-ink'}`}>{d.submitted || '—'}</td>
                                  <td className="py-1 text-brand-muted">{d.existing || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {intakeReview.diff.some((d) => d.differs) && (
                            <p className="mt-1 text-[11px] text-red-700">Highlighted values differ — on merge these are flagged for you, never overwritten.</p>
                          )}
                        </div>
                      )}
                      {/* The full captured form, grouped by section */}
                      {intakeReview.forms.map((form) => (
                        <div key={form.key}>
                          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700">{form.name}</p>
                          {form.sections.length === 0 && <p className="text-xs text-brand-muted">No answers captured.</p>}
                          {form.sections.map((sec, si) => (
                            <div key={si} className="mb-2">
                              {sec.title && <p className="text-xs font-medium text-brand-ink">{sec.title}</p>}
                              <dl className="grid grid-cols-1 gap-x-4 gap-y-0.5 sm:grid-cols-2">
                                {sec.fields.map((f, fi) => (
                                  <div key={fi} className="flex justify-between gap-2 text-xs">
                                    <dt className="text-brand-muted">{f.label}</dt>
                                    <dd className="text-right font-medium text-brand-ink">{f.value}</dd>
                                  </div>
                                ))}
                              </dl>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={approveAndPromote} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700">
                  <ClipboardCheck size={15} /> Approve &amp; Promote (new patient)
                </button>
                {likelyMatch && (
                  <button onClick={mergeInto} className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-700">
                    <GitMerge size={15} /> Merge into {likelyMatch.name}
                  </button>
                )}
                {likelyMatch && (
                  <button onClick={linkAsDependant} className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700">
                    <Users size={15} /> Add as dependant of {likelyMatch.name}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Patient header (always visible — the spine of the record) ── */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-5">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-brand-brown">{patient.title ? `${patient.title} ` : ''}{patient.full_name}</h1>
              {billingAccount && (
                <span className="inline-flex items-center gap-1 rounded-full bg-brand-cream px-2.5 py-0.5 text-xs font-semibold text-brand-brown">
                  <Wallet size={11} /> {billingAccount.account_code}
                </span>
              )}
              {patient.marital_status && (
                <span className="inline-flex items-center rounded-full bg-brand-cream px-2.5 py-0.5 text-xs font-medium text-brand-brown">{patient.marital_status}</span>
              )}
              {billingAccount?.address && (
                <span className="inline-flex max-w-[16rem] items-center truncate rounded-full bg-brand-cream px-2.5 py-0.5 text-xs font-medium text-brand-brown"
                  title={`${billingAccount.address}${billingAccount.postal_code ? ', ' + billingAccount.postal_code : ''}`}>
                  {billingAccount.address}{billingAccount.postal_code ? `, ${billingAccount.postal_code}` : ''}
                </span>
              )}
              {outstandingBalance > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-brand-danger/10 px-2.5 py-0.5 text-xs font-semibold text-brand-danger" title="Outstanding balance on this account">
                  <Wallet size={11} /> Owes {ZAR(outstandingBalance)}
                </span>
              )}
              {(medical_history?.allergies || medical_history?.chronic_conditions) && (
                <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700"
                  title={[medical_history.allergies && `Allergies: ${medical_history.allergies}`, medical_history.chronic_conditions && `Conditions: ${medical_history.chronic_conditions}`].filter(Boolean).join(' · ')}>
                  <HeartPulse size={11} /> Medical alert
                </span>
              )}
              {patient.ai_consent && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                  <Shield size={11} /> AI consent
                </span>
              )}
              {patient.no_contact && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-600 px-2.5 py-0.5 text-xs font-semibold text-white"
                  title={`No automated WhatsApp${patient.no_contact_at ? ' since ' + new Date(patient.no_contact_at).toLocaleDateString('en-ZA') : ''}${patient.no_contact_reason ? ' — ' + patient.no_contact_reason : ''}. Recalls, reminders, birthday wishes and every nudge are OFF. This patient is NOT recorded as deceased.`}>
                  🔕 No WhatsApp{patient.no_contact_reason ? ` — ${patient.no_contact_reason}` : ''}
                  <button type="button" onClick={resumeWhatsapp}
                    className="ml-0.5 rounded bg-white/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide transition hover:bg-white/30"
                    title="Turn automated WhatsApp back on for this patient">Undo</button>
                </span>
              )}
              {patient.deceased && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-800 px-2.5 py-0.5 text-xs font-semibold text-white"
                  title={`Reported deceased${patient.deceased_at ? ' on ' + new Date(patient.deceased_at).toLocaleDateString('en-ZA') : ''} — all automated messaging (birthday, recalls, reminders, nudges) is OFF for this patient.`}>
                  🕊️ Deceased — messaging off
                  <button type="button" onClick={reactivatePatient}
                    className="ml-0.5 rounded bg-white/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide transition hover:bg-white/30"
                    title="Undo — this patient is not deceased; turn automated messaging back on">Undo</button>
                </span>
              )}
            </div>
            {nextAppointment && (
              <p className="mt-1 text-xs text-brand-muted">
                Next: <strong className="text-brand-ink">{new Date(nextAppointment.start_time).toLocaleString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</strong> · {nextAppointment.reason || 'Appointment'}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => setBookOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-brand-primary-dark">
              <Calendar size={13} /> Book appointment
            </button>
            {/* The two things written FOR the patient to take away. Paul, 2026-08-12: "this
                should be easily accessible from a patient account view" — so they sit next to
                Book appointment, not two clicks away inside the digital file. */}
            <Link href={`/patients/${patient.id}/prescriptions`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand-brown hover:bg-brand-cream rounded-lg transition-colors border border-gray-200">
              <Pill size={13} /> Prescription
            </Link>
            <Link href={`/patients/${patient.id}/referrals/new`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand-brown hover:bg-brand-cream rounded-lg transition-colors border border-gray-200">
              <Send size={13} /> Referral
            </Link>
            <Link href={`/patients/${patient.id}/file`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand-brown hover:bg-brand-cream rounded-lg transition-colors border border-gray-200">
              <FolderOpen size={13} /> Digital file
            </Link>
            <button type="button" onClick={() => setEditOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand-brown hover:bg-brand-cream rounded-lg transition-colors border border-gray-200">
              <Pencil size={13} /> Edit
            </button>
            {googleReviewUrl && (patient.whatsapp_number || patient.phone) && (
              <a
                href={`https://wa.me/${((patient.whatsapp_number || patient.phone) || '').replace(/\D/g, '')}?text=${encodeURIComponent(`Hi ${patient.first_name || patient.full_name || 'there'}, thank you for visiting Dr Chalita le Roux! We'd really appreciate a quick Google review — it helps us a lot: ${googleReviewUrl}`)}`}
                target="_blank" rel="noopener noreferrer"
                title="Open WhatsApp with a pre-filled review request to send"
                className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100">
                <MessageSquare size={13} /> Request review
              </a>
            )}
            <button type="button" onClick={() => { setMergeOpen((v) => !v); setMergeTarget(null) }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand-brown hover:bg-brand-cream rounded-lg transition-colors border border-gray-200">
              <GitMerge size={13} /> Merge
            </button>
            {!patient.no_contact && !patient.deceased && (
              <button type="button" onClick={stopWhatsapp}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand-brown hover:bg-brand-cream rounded-lg transition-colors border border-gray-200"
                title="Patient asked to be taken off the list, moved away, or it's the wrong number? Stop ALL automated WhatsApp without recording them as deceased. Reversible.">
                🔕 No WhatsApp
              </button>
            )}
            {!patient.deceased && (
              <button type="button" onClick={markDeceased}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand-brown hover:bg-brand-cream rounded-lg transition-colors border border-gray-200"
                title="Told the patient has passed away? Stop ALL automated messaging (birthday, recalls, reminders, nudges). Reversible.">
                🕊️ Mark deceased
              </button>
            )}
            <button type="button" onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand-danger hover:bg-brand-danger/5 rounded-lg transition-colors border border-brand-danger/20">
              <Trash2 size={13} /> Delete
            </button>
          </div>
        </div>

        {/* General merge — combine a duplicate into one record (Paul 2026-06-26) */}
        {mergeOpen && (
          <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
            <p className="mb-1 text-sm font-semibold text-amber-900">Combine this duplicate into one record</p>
            <p className="mb-3 text-xs text-amber-800">Search for the record to <b>KEEP</b>. Everything on <b>{patient.full_name}</b> (appointments, forms, history, account) moves onto it, and this record is removed.</p>
            <PatientSearchSelect selected={mergeTarget} onSelect={setMergeTarget} excludeId={patient.id} />
            {mergeTarget && (
              <div className="mt-3 flex items-center gap-2">
                <button type="button" onClick={doMerge}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-brand-primary px-4 py-1.5 text-xs font-semibold text-white hover:bg-brand-primary-dark transition-colors">
                  <GitMerge size={13} /> Merge into {mergeTarget.name || mergeTarget.full_name}
                </button>
                <button type="button" onClick={() => setMergeOpen(false)}
                  className="rounded-lg border border-brand-border px-4 py-1.5 text-xs font-semibold text-brand-muted hover:text-brand-ink transition-colors">Cancel</button>
              </div>
            )}
          </div>
        )}

        {/* Delete confirmation inline banner */}
        {confirmDelete && (
          <div className="mb-4 rounded-xl border border-brand-danger/30 bg-brand-danger/5 px-4 py-3">
            <p className="text-sm font-medium text-brand-danger mb-3">
              Permanently delete <span className="font-bold">{patient.full_name}</span>? This cannot be undone — all appointments, conversations, treatment plans, estimates and invoices for this patient will be removed.
            </p>
            <div className="flex items-center gap-2">
              <button type="button" onClick={handleDelete}
                className="rounded-lg bg-brand-danger px-4 py-1.5 text-xs font-semibold text-white hover:bg-brand-danger/90 transition-colors">
                Yes, delete permanently
              </button>
              <button type="button" onClick={() => setConfirmDelete(false)}
                className="rounded-lg border border-brand-border px-4 py-1.5 text-xs font-semibold text-brand-muted hover:text-brand-ink transition-colors">
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          <Field label="Phone"><p className="text-sm text-gray-800">{patient.contact_hidden ? '🔒 Hidden' : (patient.phone || '—')}</p></Field>
          <Field label="Email"><p className="text-sm text-gray-800">{patient.contact_hidden ? '🔒 Hidden' : (patient.email || '—')}</p></Field>
          <Field label="Date of Birth">
            <p className="text-sm text-gray-800">{dateZA(patient.date_of_birth)}</p>
          </Field>
          <Field label="Patient Since">
            <p className="text-sm text-gray-800">{dateZA(patient.created_at)}</p>
          </Field>
          <Field label="How they heard about us">
            <p className="text-sm text-gray-800">
              {patient.referral_source || '—'}
              {patient.referral_source_detail && <span className="text-brand-muted"> · {patient.referral_source_detail}</span>}
            </p>
          </Field>
        </div>
      </div>

      {/* Persistent medical-alert banner — always visible (every tab) so the dentist never
          misses a drug allergy / condition / medication before treatment. Allergies first. */}
      {(medical_history?.allergies || medical_history?.chronic_conditions || medical_history?.current_medications) && (
        <div className="mb-4 flex flex-wrap items-start gap-x-4 gap-y-1 rounded-lg border border-red-300 bg-red-50 px-4 py-2.5 text-sm">
          <span className="inline-flex shrink-0 items-center gap-1.5 font-bold uppercase tracking-wide text-red-700"><HeartPulse size={15} /> Medical alert</span>
          {medical_history.allergies && <span className="text-red-800"><span className="font-semibold">Allergies:</span> {medical_history.allergies}</span>}
          {medical_history.chronic_conditions && <span className="text-red-800"><span className="font-semibold">Conditions:</span> {medical_history.chronic_conditions}</span>}
          {medical_history.current_medications && <span className="text-red-800"><span className="font-semibold">Meds:</span> {medical_history.current_medications}</span>}
        </div>
      )}

      {/* ── Tab bar ── */}
      <div className="mb-5 flex flex-wrap gap-1 border-b border-gray-200">
        {TABS.map(({ key, label, icon: Icon, count }) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
              tab === key
                ? 'border-brand-primary text-brand-brown'
                : 'border-transparent text-brand-muted hover:text-brand-ink'
            }`}
          >
            <Icon size={15} />
            {label}
            {count > 0 && (
              <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                tab === key ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-500'
              }`}>{count}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tab content ── */}
      {tab === 'overview' && (
        <OverviewTab
          communications={communications}
          medicalHistory={medical_history}
          scheme={scheme}
          onEditMh={() => setEditOpen(true)}
          openCourses={openCourses}
          openEstimates={openEstimates}
          openInvoices={openInvoices}
          outstandingBalance={outstandingBalance}
          showFinancials={!isDentist}
          notes={patient.notes}
          onJump={setTab}
        />
      )}

      {tab === 'treatment' && (
        <div>
          <div className="mb-3 flex justify-end">
            <button
              type="button"
              onClick={() => router.post(`/patients/${patient.id}/courses_of_treatment`)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-primary px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-primary-dark"
            >
              <Plus size={14} /> New treatment plan
            </button>
          </div>
        <RecordList
          title="Treatment plans (Courses of Treatment)"
          emptyText="No treatment plans yet for this patient."
          items={courses}
          renderRow={(c) => (
            <RecordRow
              key={c.id}
              href={`/courses-of-treatment/${c.id}`}
              icon={ClipboardPlus}
              primary={c.description || `Course #${c.id}`}
              secondary={`${c.item_count} item${c.item_count === 1 ? '' : 's'} · est. ${ZAR(c.estimated_total)} · done ${ZAR(c.completed_total)}`}
              status={c.status}
              meta={dateZA(c.created_at, { day: 'numeric', month: 'short', year: 'numeric' })}
            />
          )}
        />
        </div>
      )}

      {tab === 'estimates' && (
        <div>
          {/* Treatment journey builder (Paul 2026-07-07) — combine + sequence estimates into one phased plan */}
          <div className="mb-4 rounded-xl border border-brand-border bg-white p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-brand-ink">Treatment journey</h3>
              <div className="flex items-center gap-2">
                {templates.length > 0 && (
                  <select defaultValue="" onChange={(e) => { const id = e.target.value; e.target.value = ''; if (id && window.confirm('Build a new plan for this patient from that template?')) router.post('/journeys/from_template', { patient_id: patient.id, template_id: id }) }} className="rounded-lg border border-brand-border px-2 py-1.5 text-xs text-brand-ink">
                    <option value="">Build from template…</option>
                    {templates.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.phases})</option>)}
                  </select>
                )}
                <button type="button" onClick={buildJourney} disabled={journeySel.length === 0} className="rounded-lg bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-primary-dark disabled:opacity-40">Build from {journeySel.length} selected</button>
              </div>
            </div>
            <p className="mt-1 text-xs text-brand-muted">Tick estimates in the order the patient should do them, then build one combined, phased plan to preview &amp; send.</p>
            <div className="mt-2 flex flex-col gap-1">
              {/* Done work can't be part of a plan you're about to propose (Paul 2026-07-16). */}
              {estimates.filter((e) => e.status !== 'cancelled' && !e.completed).map((e) => {
                const pos = journeySel.indexOf(e.id)
                return (
                  <button key={e.id} type="button" onClick={() => toggleJourney(e.id)} className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-left text-xs ${pos >= 0 ? 'border-brand-primary bg-brand-surface' : 'border-brand-border bg-white hover:bg-brand-surface/50'}`}>
                    <span className={`flex h-5 w-5 flex-none items-center justify-center rounded-full border text-[10px] font-bold ${pos >= 0 ? 'border-brand-primary bg-brand-primary text-white' : 'border-brand-border text-brand-muted'}`}>{pos >= 0 ? pos + 1 : ''}</span>
                    <span className="flex-1 truncate text-brand-ink">{e.title || e.number}</span>
                    <span className="tabular-nums text-brand-muted">{ZAR(e.total)}</span>
                  </button>
                )
              })}
            </div>
            {journeys.length > 0 && (
              <div className="mt-3 border-t border-brand-border pt-2">
                <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-brand-muted">Built journeys</p>
                {journeys.map((j) => (
                  <div key={j.id} className="flex items-center gap-2 py-1 text-xs">
                    <span className="flex-1 truncate text-brand-ink">{j.title} · {j.phase_count} phase{j.phase_count === 1 ? '' : 's'} · {j.total_high ? `${ZAR(j.total)} – ${ZAR(j.total_high)}` : ZAR(j.total)}{j.status === 'sent' ? ' · sent' : ''}{j.accepted ? ' · ✅ accepted' : ''}{j.response_reason ? ` · ⚠ ${j.response_reason}${j.response_note ? ' (' + j.response_note + ')' : ''}` : ''}</span>
                    <button type="button" onClick={() => router.visit(`/journeys/${j.id}/edit`)} className="rounded border border-brand-border px-2 py-0.5 font-semibold text-brand-ink hover:bg-brand-surface">Edit</button>
                    <a href={j.preview_url} target="_blank" rel="noreferrer" className="rounded border border-brand-border px-2 py-0.5 font-semibold text-brand-ink hover:bg-brand-surface">Preview</a>
                    <button type="button" onClick={() => sendJourney(j.id)} title="Send on WhatsApp" className="rounded bg-emerald-600 px-2 py-0.5 font-semibold text-white hover:opacity-90">WhatsApp</button>
                    <button type="button" onClick={() => emailJourney(j.id)} title={patient.email ? `Email to ${patient.email}` : 'No email on file'} className="rounded bg-brand-primary px-2 py-0.5 font-semibold text-white hover:opacity-90">Email</button>
                    <button type="button" onClick={() => removeJourney(j.id)} className="rounded border border-brand-border px-2 py-0.5 text-brand-muted hover:bg-brand-surface">✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="mb-3 flex flex-wrap justify-end gap-2">
            {latestScan && (
              <button
                type="button"
                onClick={draftFromScan}
                disabled={draftingScan}
                title={`Read the latest uploaded scan (${latestScan.date || latestScan.file_name}) and draft the estimate(s) from it`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-brand-primary bg-white px-3 py-1.5 text-sm font-semibold text-brand-primary shadow-sm transition hover:bg-brand-primary/5 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Sparkles size={14} className={draftingScan ? 'animate-pulse' : ''} />
                {draftingScan ? 'Reading scan…' : `Draft from latest scan (${latestScan.date || 'uploaded file'})`}
              </button>
            )}
            {latestWritten && (
              <button
                type="button"
                onClick={draftFromWritten}
                disabled={draftingWritten}
                title={`Read the latest hand-written chart (${latestWritten.label || latestWritten.date}) and draft the estimate(s) from it`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-brand-primary bg-white px-3 py-1.5 text-sm font-semibold text-brand-primary shadow-sm transition hover:bg-brand-primary/5 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Sparkles size={14} className={draftingWritten ? 'animate-pulse' : ''} />
                {draftingWritten ? 'Reading chart…' : `Draft from latest written chart (${latestWritten.label || latestWritten.date || ''})`}
              </button>
            )}
            <button
              type="button"
              onClick={() => router.post(`/patients/${patient.id}/estimates`)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-primary px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-primary-dark"
            >
              <Plus size={14} /> New estimate
            </button>
          </div>
        <RecordList
          title="Estimates"
          emptyText="No estimates yet for this patient."
          items={estimates}
          renderRow={(e) => (
            <div
              key={e.id}
              onContextMenu={(ev) => { ev.preventDefault(); setEstMenu({ x: ev.clientX, y: ev.clientY, est: e }) }}
              className={e.status === 'cancelled' || e.completed ? 'opacity-40 grayscale' : ''}
              title="Right-click for options (cancel / activate / convert to invoice)"
            >
              <RecordRow
                href={`/estimates/${e.id}`}
                icon={FileText}
                primary={
                  <span className="inline-flex flex-wrap items-center gap-2">
                    <span>{e.title || e.number || `Estimate #${e.id}`}</span>
                    {/* Work that's been invoiced reads as DONE, not outstanding (Paul 2026-07-16). */}
                    {e.completed && <span className="rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-green-700">✓ Done — work invoiced</span>}
                    {e.ai && !e.completed && <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-700">⚠ AI estimated — please review</span>}
                    {/* A WhatsApp send of this estimate did NOT land on the patient's phone (Paul 2026-08-14) —
                        reception must send it manually. Also flags the patient's diary block red. */}
                    {e.whatsapp_delivery_failed && <span title="This estimate was sent on WhatsApp but did NOT deliver to the patient. Please send it manually (check their WhatsApp number)." className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">⚠ Not delivered — send manually</span>}
                  </span>
                }
                secondary={e.number || `Valid until ${dateZA(e.valid_until)}`}
                amount={ZAR(e.total)}
                status={e.status}
                meta={dateZA(e.created_at, { day: 'numeric', month: 'short', year: 'numeric' })}
              />
            </div>
          )}
        />
        {estMenu && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setEstMenu(null)} onContextMenu={(ev) => { ev.preventDefault(); setEstMenu(null) }} />
            <div className="fixed z-50 w-56 overflow-hidden rounded-lg border border-brand-border bg-white py-1 text-sm shadow-xl" style={{ top: Math.min(estMenu.y, window.innerHeight - 170), left: Math.min(estMenu.x, window.innerWidth - 230) }}>
              <div className="border-b border-brand-border px-3 py-1.5 text-xs font-semibold text-brand-muted truncate">{estMenu.est.number || estMenu.est.title}</div>
              <a href={`/estimates/${estMenu.est.id}`} className="block px-3 py-2 hover:bg-brand-surface">✎ Open estimate</a>
              {estMenu.est.status !== 'cancelled' && (
                <>
                  <button className="block w-full px-3 py-2 text-left text-emerald-700 hover:bg-brand-surface" onClick={() => estAction('send_case_page', estMenu.est, 'Plan link sent on WhatsApp', 'Send this treatment plan to the patient on WhatsApp — an interactive link they can view, accept and download the PDF from?')}>🔗 Send to patient (WhatsApp)</button>
                  <button className="block w-full px-3 py-2 text-left text-emerald-700/80 hover:bg-brand-surface" onClick={() => estAction('send_whatsapp', estMenu.est, 'Estimate PDF sent on WhatsApp', 'Send the estimate as a PDF attachment only (some phones struggle to open WhatsApp document attachments)?')}>🧾 Send as PDF only</button>
                  <button className="block w-full px-3 py-2 text-left text-brand-primary hover:bg-brand-surface" onClick={() => estAction('send_email', estMenu.est, 'Quotation emailed to patient', patient.email ? `Email this quotation (PDF) to ${patient.full_name} at ${patient.email}? Some patients prefer email.` : `${patient.full_name} has no email on file — add one first, or send on WhatsApp.`)}>📧 Send on email</button>
                </>
              )}
              {/* Duplicate → clone the plan as a fresh draft on the same patient (Paul 2026-08-28) */}
              <button className="block w-full px-3 py-2 text-left hover:bg-brand-surface" onClick={() => estAction('duplicate', estMenu.est, 'Estimate duplicated', 'Duplicate this estimate as a fresh draft on the same patient? A new estimate number is issued.')}>⧉ Duplicate</button>
              {estMenu.est.status === 'cancelled' ? (
                <button className="block w-full px-3 py-2 text-left text-emerald-600 hover:bg-brand-surface" onClick={() => estAction('activate', estMenu.est, 'Estimate reactivated')}>↩ Activate</button>
              ) : (
                <button className="block w-full px-3 py-2 text-left text-brand-danger hover:bg-brand-surface" onClick={() => estAction('cancel', estMenu.est, 'Estimate cancelled')}>✕ Cancel (dark)</button>
              )}
              <button className="block w-full px-3 py-2 text-left hover:bg-brand-surface" onClick={() => estAction('accept_and_invoice', estMenu.est, 'Converted to invoice', 'Convert this estimate to an invoice? This creates a real invoice.')}>→ Convert to invoice</button>
            </div>
          </>
        )}
        </div>
      )}

      {tab === 'invoices' && (
        <div>
          <div className="mb-3 flex justify-end">
            <button
              type="button"
              onClick={() => router.post(`/patients/${patient.id}/estimates`, { intent: 'invoice' })}
              title="Start a new invoice — same editor & tools as an estimate (describe → AI fills codes, pick provider), labelled Invoice and kept out of the estimates tracker"
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-primary px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-primary-dark"
            >
              <Plus size={14} /> New invoice
            </button>
          </div>
        <RecordList
          title="Invoices"
          emptyText="No invoices yet for this patient."
          items={invoices}
          headerRight={
            <span className="text-sm font-semibold text-brand-danger">
              Outstanding: {ZAR(accountSummary.outstanding)}
            </span>
          }
          renderRow={(i) => (
            <RecordRow
              key={i.id}
              href={`/invoices/${i.id}`}
              icon={Receipt}
              primary={i.number || `Invoice #${i.id}`}
              secondary={i.status === 'void' ? 'Void — cancelled' : i.status === 'written_off' ? 'Written off' : i.balance > 0 ? `Balance ${ZAR(i.balance)}` : 'Settled'}
              amount={ZAR(i.total)}
              status={i.status}
              meta={dateZA(i.invoice_date || i.created_at, { day: 'numeric', month: 'short', year: 'numeric' })}
            />
          )}
        />
        </div>
      )}

      {tab === 'chart' && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <a
                href={`/patients/${patient.id}/chart`}
                className="inline-flex items-center gap-2 rounded-2xl bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-primary-dark"
              >
                <Grid3x3 size={16} /> Open interactive chart — tap teeth, chart &amp; mark done →
              </a>
              <a
                href={`/patients/${patient.id}/perio`}
                className="inline-flex items-center gap-2 rounded-2xl border border-brand-border bg-white px-4 py-2.5 text-sm font-semibold text-brand-ink shadow-sm transition-colors hover:bg-brand-surface"
                title="Periodontal chart — probing depths, bleeding, mobility, furcation"
              >
                Perio chart →
              </a>
            </div>
            <span className="text-xs text-brand-muted">Read-only quick view below</span>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs text-brand-muted">
            <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-red-500" /> Needs work (outstanding)</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-gray-800" /> Done / existing</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded border border-brand-border bg-white" /> Healthy / unrecorded</span>
          </div>
          <Odontogram chart={toothChart} details={toothChartDetail} />
          {Object.keys(toothChart).length === 0 && (
            <p className="text-sm text-brand-muted">No charted work yet for this patient — planned treatment shows red, completed/existing work shows black.</p>
          )}
        </div>
      )}

      {tab === 'imaging' && (
        <div>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-medium text-brand-muted">
              {imaging.length} SIDEXIS scan{imaging.length === 1 ? '' : 's'} indexed{imaging.length ? ' — click any thumbnail to open full size' : ''}
            </h3>
            <a
              href={`/patients/${patient.id}/scan-viewer`}
              target="_blank"
              rel="noopener noreferrer"
              title="Open the full 2D/3D viewer (CBCT rotate, MPR, measure) — on the practice network"
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-ink px-3 py-2 text-sm font-semibold text-white transition hover:opacity-90"
            >
              <Scan size={15} /> Open advanced scan viewer →
            </a>
          </div>
          {imaging.length === 0 ? (
            <p className="text-sm text-brand-muted">No preview thumbnails indexed yet — the advanced viewer above reads the full DICOM archive (2D &amp; CBCT) directly.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {imaging.map((s) => (
                <div
                  key={s.id}
                  className="group relative block w-full overflow-hidden rounded-lg border border-brand-border bg-white text-left transition hover:shadow-md"
                >
                  {/* Mark findings — opens the co-diagnosis markup page for this scan (Phase 2A). */}
                  <Link
                    href={`/patients/${patient.id}/imaging/${s.id}/markup`}
                    onClick={(e) => e.stopPropagation()}
                    title="Mark findings on this scan"
                    className="absolute right-1.5 top-1.5 z-10 inline-flex items-center gap-1 rounded-md bg-white/90 px-2 py-1 text-[11px] font-semibold text-brand-ink opacity-0 shadow transition group-hover:opacity-100 hover:bg-white"
                  >
                    <Pencil size={12} /> Mark
                  </Link>
                  <button
                    type="button"
                    onClick={() => setDicomStudy(s)}
                    className="block w-full text-left"
                  >
                    {s.has_image ? (
                      <img
                        src={`/imaging/${s.id}/image`}
                        alt={s.modality_label}
                        loading="lazy"
                        className="h-32 w-full bg-black object-contain"
                      />
                    ) : (
                      <div className="flex h-32 w-full items-center justify-center bg-gray-100">
                        <Scan className="h-8 w-8 text-brand-muted" />
                      </div>
                    )}
                    <div className="p-2">
                      <div className="text-xs font-medium text-brand-ink">{s.modality_label}</div>
                      <div className="text-xs text-brand-muted">
                        {dateZA(s.captured_at, { day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                      {s.notes && <div className="truncate text-[11px] text-brand-muted">{s.notes}</div>}
                    </div>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'account' && (
        <AccountTab account={billingAccount} summary={accountSummary} currentPatientId={patient.id} scheme={scheme} headName={patient.full_name} />
      )}

      {tab === 'notes' && <NotesTab notes={notes} patientId={patient.id} />}

      {tab === 'history' && (
        <HistoryTab appointments={appointments} conversations={conversations} communications={communications} documents={documents} writeSessions={write_sessions} chartedVisits={charted_visits} signedPack={signedPack} intakeInProgress={intakeInProgress} patientId={patient.id} />
      )}

      {dicomStudy && <DicomViewer study={dicomStudy} onClose={() => setDicomStudy(null)} />}

      <PatientFormModal
        open={editOpen}
        mode="edit"
        patient={patient}
        accountCode={billingAccount?.account_code || ''}
        addressLine1={billingAccount?.address_line1 || ''}
        addressLine2={billingAccount?.address_line2 || ''}
        city={billingAccount?.city || ''}
        postalCode={billingAccount?.postal_code || ''}
        medicalHistory={medical_history}
        scheme={scheme}
        schemes={schemes}
        bloodTypes={medical_history?.blood_types}
        referralSources={referral_sources}
        onClose={() => setEditOpen(false)}
      />
      <AppointmentFormModal
        mode="create"
        open={bookOpen}
        onClose={() => setBookOpen(false)}
        patients={minimalPatientList}
        providers={providers}
        prefillPatient={{ id: patient.id, full_name: patient.full_name, name: patient.full_name, account_code: patient.account_code }}
      />
    </DashboardLayout>
  )
}

// ── Communications ────────────────────────────────────────────────
//
// Everything this patient has been told and has said back, on ONE timeline, whichever line it
// travelled on: the AI receptionist's 083 WhatsApp and reception's own 071 phone (mirrored in from
// WhatsApp Web). The 071 half is deliberately kept OUT of the Conversations queue — hundreds of
// reception chats there would bury the threads still waiting on an answer — so this page is the one
// place the two halves are put back together (Paul 2026-09-10).

// "Clinic" is anything the practice sent, from either line; "Patient" is anything they sent back.
function commWho(m) {
  if (m.role !== 'assistant') return 'Patient'
  return m.via === '071' ? 'Reception (071 phone)' : 'Ivory'
}

function commWhen(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
}

// The one line reception wants at a glance: what was said last, by whom, when.
function LatestCommunication({ communications = [], onJump }) {
  if (!communications.length) return null
  const recent = communications.slice(-3)
  const last = recent[recent.length - 1]
  return (
    <div className="rounded-xl border border-brand-border bg-white p-4">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-brand-brown">
          <MessageSquare size={15} className="text-brand-primary" /> Last conversation
          <span className="font-normal text-brand-muted">· {commWhen(last.at)}</span>
        </h2>
        <button type="button" onClick={() => onJump && onJump('history')}
          className="text-xs font-semibold text-brand-primary hover:underline">
          See all {communications.length} messages
        </button>
      </div>
      <div className="space-y-1.5">
        {recent.map((m, i) => (
          <div key={i} className="flex gap-2 text-sm">
            <span className={`mt-px w-40 flex-shrink-0 text-xs font-semibold ${m.role === 'assistant' ? 'text-brand-primary' : 'text-brand-brown'}`}>
              {commWho(m)}
            </span>
            <p className="min-w-0 flex-1 whitespace-pre-wrap break-words text-brand-ink line-clamp-3">{m.content}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

// The full readable record, newest LAST so it reads like the chat it is.
function CommunicationsPanel({ communications = [] }) {
  const [showAll, setShowAll] = useState(false)
  if (!communications.length) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <h2 className="mb-1 text-base font-semibold text-brand-brown">Communications</h2>
        <p className="text-sm text-gray-400">Nothing has been sent to or received from this patient yet.</p>
      </div>
    )
  }
  const shown = showAll ? communications : communications.slice(-30)
  const hidden = communications.length - shown.length
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <h2 className="mb-1 text-base font-semibold text-brand-brown">Communications</h2>
      <p className="mb-3 text-xs text-gray-400">
        Every message with this patient, on one timeline — the AI receptionist&rsquo;s WhatsApp line and
        reception&rsquo;s own 071 phone together.
      </p>
      {hidden > 0 && (
        <button type="button" onClick={() => setShowAll(true)}
          className="mb-3 w-full rounded-lg border border-brand-border bg-brand-surface py-1.5 text-xs font-semibold text-brand-primary hover:bg-brand-accent/20">
          Show {hidden} earlier {hidden === 1 ? 'message' : 'messages'}
        </button>
      )}
      <div className="space-y-2">
        {shown.map((m, i) => {
          const clinic = m.role === 'assistant'
          return (
            <div key={i} className={`flex ${clinic ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-2 ${clinic ? 'bg-[#d9fdd3]' : 'border border-gray-200 bg-gray-50'}`}>
                <p className="whitespace-pre-wrap break-words text-sm text-brand-ink">{m.content}</p>
                <p className="mt-1 flex items-center justify-end gap-1.5 text-[10px] text-brand-muted">
                  {m.via === '071' && (
                    <span title="Sent or received on the practice phone (071) WhatsApp — reception, not the AI"
                      className="rounded-full bg-brand-accent/25 px-1.5 py-px font-semibold text-brand-ink/70">
                      071 phone
                    </span>
                  )}
                  {commWho(m)} · {commWhen(m.at)}
                </p>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Overview tab ──────────────────────────────────────────────────
function OverviewTab({ medicalHistory, scheme = null, onEditMh, openCourses, openEstimates, openInvoices, outstandingBalance, showFinancials = true, notes, onJump, communications = [] }) {
  const hasShortcuts = openCourses.length > 0 || openEstimates.length > 0 || (showFinancials && (openInvoices.length > 0 || outstandingBalance > 0))
  return (
    <div className="space-y-5">
      <LatestCommunication communications={communications} onJump={onJump} />
      {hasShortcuts && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {openCourses.length > 0 && (
            <button onClick={() => onJump('treatment')}
              className="group flex items-center justify-between rounded-xl border border-brand-border bg-white px-3 py-2.5 hover:bg-brand-surface text-left">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-700"><ClipboardPlus size={15} /></span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-brand-muted">Active treatment</p>
                  <p className="text-sm font-medium text-brand-ink">{openCourses[0].item_count} item{openCourses[0].item_count === 1 ? '' : 's'} · {openCourses[0].status}</p>
                </div>
              </div>
              <ArrowRight size={14} className="text-brand-muted group-hover:text-brand-ink" />
            </button>
          )}
          {openEstimates.length > 0 && (
            <button onClick={() => onJump('estimates')}
              className="group flex items-center justify-between rounded-xl border border-brand-border bg-white px-3 py-2.5 hover:bg-brand-surface text-left">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50 text-amber-700"><FileText size={15} /></span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-brand-muted">Open estimate</p>
                  <p className="text-sm font-medium text-brand-ink">{openEstimates[0].number} · {ZAR(openEstimates[0].total)}</p>
                </div>
              </div>
              <ArrowRight size={14} className="text-brand-muted group-hover:text-brand-ink" />
            </button>
          )}
          {showFinancials && (openInvoices.length > 0 || outstandingBalance > 0) && (
            <button onClick={() => onJump('invoices')}
              className="group flex items-center justify-between rounded-xl border border-brand-border bg-white px-3 py-2.5 hover:bg-brand-surface text-left">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700"><Wallet size={15} /></span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-brand-muted">Outstanding</p>
                  <p className="text-sm font-medium text-brand-danger">{ZAR(outstandingBalance)} on {openInvoices.length} invoice{openInvoices.length === 1 ? '' : 's'}</p>
                </div>
              </div>
              <ArrowRight size={14} className="text-brand-muted group-hover:text-brand-ink" />
            </button>
          )}
        </div>
      )}

      <MedicalHistoryPanel mh={medicalHistory} scheme={scheme} onEdit={onEditMh} />

      {notes && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Notes</p>
          <p className="text-sm text-gray-700 whitespace-pre-line">{notes}</p>
        </div>
      )}
    </div>
  )
}

// ── Generic record list + row (treatment / estimates / invoices) ───
function RecordList({ title, items, renderRow, emptyText, headerRight }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-base font-semibold text-brand-brown">{title}</h2>
        {headerRight}
      </div>
      {items.length > 0 ? (
        <div className="space-y-2">{items.map(renderRow)}</div>
      ) : (
        <p className="text-sm text-gray-400">{emptyText}</p>
      )}
    </div>
  )
}

function RecordRow({ href, icon: Icon, primary, secondary, amount, status, meta }) {
  return (
    <Link href={href} className="group flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3 hover:bg-brand-cream transition-colors">
      <div className="flex items-center gap-3 min-w-0">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white text-brand-taupe border border-gray-200"><Icon size={16} /></span>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-gray-800">{primary}</p>
          <p className="truncate text-xs text-gray-400">{secondary}</p>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        {amount && <span className="text-sm font-semibold text-brand-ink">{amount}</span>}
        {status && (
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-600'}`}>
            {String(status).replace('_', ' ')}
          </span>
        )}
        <span className="hidden sm:block text-xs text-gray-400 w-20 text-right">{meta}</span>
        <ArrowRight size={14} className="text-brand-muted group-hover:text-brand-ink" />
      </div>
    </Link>
  )
}

// ── Account tab ───────────────────────────────────────────────────
function AccountTab({ account, summary, currentPatientId, scheme = null, headName = '' }) {
  const [addOpen, setAddOpen] = useState(false)
  // TAKE PAYMENT from the patient's Account tab (Paul 2026-09-09) — the same modal the account ledger and
  // invoices use: card / cash / EFT / discount, dated, allocated oldest-invoice-first or banked as a deposit.
  const [payOpen, setPayOpen] = useState(false)
  const netOutstanding = summary?.net_outstanding ?? summary?.outstanding ?? 0
  // Tick a member as head of the account → everyone else on the account becomes a dependant. The
  // statement is addressed to the head + deposits/refunds attribute to them. (Paul 2026-07-01)
  const setHead = (m) => {
    if (m.is_head) return
    if (!window.confirm(`Make ${m.name} the head of this account?\n\n• The statement is addressed to them and deposits/refunds are attributed to them.\n• Everyone else on the account becomes a dependant.`)) return
    router.post(`/accounts/${account.id}/set_head`, { patient_id: m.id }, { preserveScroll: true, onSuccess: () => toast.success(`${m.name} is now the head of the account`) })
  }
  if (!account) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-base font-semibold text-brand-brown mb-2">Billing account</h2>
        <p className="text-sm text-gray-400">No billing account linked to this patient yet. Add one from the Edit form.</p>
      </div>
    )
  }
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SummaryStat label="Total billed" value={ZAR(summary.total_billed)} />
        <SummaryStat label="Total paid" value={ZAR(summary.total_paid)} tone="emerald" />
        {(() => {
          // An overpayment banks as account credit → outstanding reads NEGATIVE (in the patient's
          // favour), and names the payment that caused it (Paul 2026-07-30). Fall back to the raw
          // invoice balance when the payload predates net_outstanding.
          const net = summary.net_outstanding ?? summary.outstanding
          const cs = summary.credit_source
          const tone = net < 0 ? 'emerald' : net > 0 ? 'danger' : 'muted'
          const val = net < 0 ? `-${ZAR(Math.abs(net))}` : ZAR(net)
          const sub = net < 0 && cs
            ? `${ZAR(cs.amount)} credit from a ${cs.method} ${cs.kind === 'deposit' ? 'deposit' : 'payment'}${cs.date ? ` on ${cs.date}` : ''}`
            : null
          return <SummaryStat label="Outstanding" value={val} tone={tone} sub={sub} />
        })()}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="mb-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Wallet size={16} className="text-brand-taupe" />
            <h2 className="text-base font-semibold text-brand-brown">Billing account</h2>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setPayOpen(true)}
              className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-700"
              title="Take a payment on this account — card, cash, EFT or discount; allocated to the oldest open invoices or banked as a deposit">
              <Wallet size={13} /> Take payment
            </button>
            <Link href={`/accounts/${account.id}`}
              className="inline-flex items-center gap-1 rounded-lg bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-brand-primary-dark"
              title="Open the account ledger + print a statement">
              <Receipt size={13} /> View account &amp; statement
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
          <Field label="Account code"><p className="text-sm font-mono text-gray-800">{account.account_code}</p></Field>
          <Field label="Billing name"><p className="text-sm text-gray-800">{account.billing_name || '—'}</p></Field>
          <Field label="Members on account"><p className="text-sm text-gray-800">{account.members.length}</p></Field>
        </div>

        <div className="mt-5">
          <div className="mb-1 flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Patients on this account</p>
            <button type="button" onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-1 rounded-lg border border-brand-primary px-2.5 py-1 text-xs font-semibold text-brand-primary transition hover:bg-brand-primary/5"
              title="Add a dependant to this account — inherits the medical aid">
              <Plus size={13} /> Add dependant
            </button>
          </div>
          <p className="mb-2 text-[11px] text-gray-400">Tick the <strong className="text-amber-700">★ head of the account</strong> — everyone else becomes a dependant.</p>
          {account.members.length > 0 ? (
            <div className="space-y-1.5">
              {account.members.map((m) => (
                <div key={m.id}
                  className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors ${
                    m.is_head ? 'bg-amber-50 ring-1 ring-amber-200' : m.id === currentPatientId ? 'bg-brand-cream text-brand-brown font-medium' : 'bg-gray-50 text-gray-700'
                  }`}>
                  <label className="flex flex-1 items-center gap-2.5 cursor-pointer" title={m.is_head ? 'Head of the account' : `Make ${m.name} the head of the account`}>
                    <input type="radio" name="acct_head" checked={!!m.is_head} onChange={() => setHead(m)} className="h-4 w-4 cursor-pointer accent-amber-600" />
                    <Users size={13} className="text-brand-taupe" />
                    <span>{m.name}</span>
                  </label>
                  <span className="flex items-center gap-2">
                    {m.is_head
                      ? <span className="rounded-full border border-amber-200 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">★ Head</span>
                      : <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">Dependant</span>}
                    <Link href={`/patients/${m.id}`} className="inline-flex items-center gap-0.5 text-[11px] text-brand-primary hover:underline"><Pencil size={11} /> Open</Link>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">No members linked yet.</p>
          )}
        </div>
      </div>
      <AddDependantModal open={addOpen} onClose={() => setAddOpen(false)} headId={currentPatientId} headName={headName} scheme={scheme} />
      <PaymentModal open={payOpen} onClose={() => setPayOpen(false)}
        account={{ id: account.id, outstanding: netOutstanding }}
        onSaved={() => router.reload({ only: [ 'account_summary', 'open_invoices', 'billing_account', 'invoices' ], preserveScroll: true })} />
    </div>
  )
}

// Add a dependant straight onto the family account — inherits the main member's medical aid
// (scheme, member number, plan, main-member name + ID) so it pulls through to invoices/estimates
// without re-typing (Paul 2026-06-26). Their own patient record, editable like any other.
function AddDependantModal({ open, onClose, headId, headName, scheme }) {
  const [f, setF] = useState({ first_name: '', last_name: '', date_of_birth: '', id_number: '', phone: '', dependant_code: '', relationship: 'child', inherit_medical_aid: true })
  const [saving, setSaving] = useState(false)
  if (!open) return null
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target?.type === 'checkbox' ? e.target.checked : e.target.value }))
  const save = () => {
    if (!f.first_name.trim()) { toast.error('Enter the dependant’s first name'); return }
    setSaving(true)
    router.post(`/patients/${headId}/add_dependant`, { dependant: f }, {
      onSuccess: () => { toast.success('Dependant added'); onClose() },
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Could not add dependant'),
      onFinish: () => setSaving(false),
    })
  }
  const inp = 'mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-primary focus:outline-none'
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-brand-brown">Add dependant</h3>
        <p className="mt-0.5 text-sm text-brand-muted">A new patient on {headName}&rsquo;s account.</p>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <label className="text-xs font-medium text-gray-500">First name<input value={f.first_name} onChange={set('first_name')} className={inp} /></label>
          <label className="text-xs font-medium text-gray-500">Surname<input value={f.last_name} onChange={set('last_name')} placeholder="(defaults to family surname)" className={inp} /></label>
          <label className="text-xs font-medium text-gray-500">Date of birth<input type="date" value={f.date_of_birth} onChange={set('date_of_birth')} className={inp} /></label>
          <label className="text-xs font-medium text-gray-500">ID number<input value={f.id_number} onChange={set('id_number')} placeholder="optional for minors" className={inp} /></label>
          <label className="text-xs font-medium text-gray-500">Cell number<input value={f.phone} onChange={set('phone')} placeholder="optional" className={inp} /></label>
          <label className="text-xs font-medium text-gray-500">Relationship
            <select value={f.relationship} onChange={set('relationship')} className={inp}>
              <option value="child">Child</option><option value="spouse">Spouse</option>
              <option value="dependant">Dependant</option><option value="guardian">Guardian</option>
            </select>
          </label>
          <label className="text-xs font-medium text-gray-500">Dependant code<input value={f.dependant_code} onChange={set('dependant_code')} placeholder="e.g. 02" className={inp} /></label>
        </div>
        <label className="mt-4 flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm">
          <input type="checkbox" checked={f.inherit_medical_aid} onChange={set('inherit_medical_aid')} className="mt-0.5" />
          <span>
            <span className="font-medium text-gray-800">Copy the main member&rsquo;s medical aid</span>
            <span className="block text-xs text-gray-500">{scheme?.scheme_name ? `Inherits ${scheme.scheme_name}${scheme.plan_option ? ` · ${scheme.plan_option}` : ''} (member ${scheme.membership_number || '—'}) + main-member ID.` : 'This account has no medical aid on file — nothing to copy.'}</span>
          </span>
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="rounded-lg bg-brand-primary px-4 py-2 text-sm font-semibold text-white hover:bg-brand-primary-dark disabled:opacity-50">Add dependant</button>
        </div>
      </div>
    </div>
  )
}

function SummaryStat({ label, value, tone = 'ink', sub = null }) {
  const toneClass = {
    ink: 'text-brand-ink', emerald: 'text-emerald-700', danger: 'text-brand-danger', muted: 'text-brand-muted',
  }[tone]
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-lg font-bold ${toneClass}`}>{value}</p>
      {sub && <p className="mt-0.5 text-[11px] leading-tight text-emerald-700">{sub}</p>}
    </div>
  )
}

// ── History tab (appointments + conversations) ────────────────────
// Renders saved pen strokes (vector JSON) read-only on a canvas.
function InkCanvas({ ink }) {
  const ref = useRef(null)
  useEffect(() => {
    let strokes = []
    try { strokes = JSON.parse(ink) } catch { return }
    if (!Array.isArray(strokes) || !strokes.length) return
    const cv = ref.current; if (!cv) return
    let maxX = 280, maxY = 60
    strokes.forEach((s) => (s.pts || []).forEach((p) => { if (p[0] > maxX) maxX = p[0]; if (p[1] > maxY) maxY = p[1] }))
    cv.width = Math.ceil(maxX + 30); cv.height = Math.ceil(maxY + 30)
    const cx = cv.getContext('2d'); cx.lineCap = 'round'; cx.lineJoin = 'round'
    cx.fillStyle = '#fffdf7'; cx.fillRect(0, 0, cv.width, cv.height)
    strokes.forEach((s) => {
      cx.strokeStyle = s.color || '#1f2937'
      for (let i = 1; i < (s.pts || []).length; i++) {
        const a = s.pts[i - 1], b = s.pts[i]
        cx.beginPath(); cx.lineWidth = (s.size || 3) * (0.5 + (b[2] || 0.5)); cx.moveTo(a[0], a[1]); cx.lineTo(b[0], b[1]); cx.stroke()
      }
    })
  }, [ink])
  return <canvas ref={ref} className="max-w-full rounded-lg border border-brand-border" />
}

// Dated personal notes (typed + pen) written on the dental chart.
function NotesTab({ notes = [], patientId }) {
  if (!notes.length) {
    return (
      <div className="rounded-2xl border border-brand-border bg-white p-8 text-center">
        <StickyNote size={28} className="mx-auto mb-2 text-brand-muted" />
        <p className="text-sm text-brand-muted">No notes yet. Notes the dentist writes on the dental chart (typed or with the pen) appear here, grouped by date.</p>
        <Link href={`/patients/${patientId}/chart`} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-brand-primary">Open the chart to add notes <ArrowRight size={14} /></Link>
      </div>
    )
  }
  return (
    <div className="space-y-4">
      {notes.map((n) => (
        <div key={n.id} className="rounded-2xl border border-brand-border bg-white p-4">
          <div className="mb-2 flex items-center gap-2 text-sm font-bold text-brand-ink">
            <Calendar size={14} className="text-brand-muted" /> {n.date}
          </div>
          {n.content ? <div className="mb-2 whitespace-pre-wrap text-sm leading-relaxed text-brand-ink">{n.content}</div> : null}
          {n.ink ? <InkCanvas ink={n.ink} /> : null}
          {!n.content && !n.ink ? <div className="text-xs text-brand-muted">(empty)</div> : null}
        </div>
      ))}
    </div>
  )
}

// Folder picker + hidden file input + one button. Posts multipart to DocumentsController#create, which
// redirects back to this page so the new file appears in the list below (no partial reload — the
// live-refresh audit locks which props may be partially reloaded, and `documents` is not one of them).
function UploadPatientFile({ patientId }) {
  const inputRef = useRef(null)
  const [folder, setFolder] = useState('other')
  const [busy, setBusy] = useState(false)
  const FOLDERS = [
    ['other', 'Other'],
    ['chart_scans', 'Scanned Charts & Estimates'],
    ['consent_forms', 'Consent Forms'],
    ['referral_letters', 'Referral Letters'],
    ['correspondence', 'Correspondence'],
    ['personal', 'Personal Documents'],
    ['treatment_plans', 'Treatment Plans'],
    ['befores_afters', 'Befores & Afters'],
    ['sidexis_scans', 'Sidexis Scans'],
    ['aligners', 'Active Aligners'],
  ]
  const onPick = (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !patientId) return
    if (file.size > 25 * 1024 * 1024) { toast.error('That file is over 25 MB'); return }
    const fd = new FormData()
    fd.append('file', file)
    fd.append('folder', folder)
    setBusy(true)
    router.post(`/patients/${patientId}/documents`, fd, {
      forceFormData: true,
      preserveScroll: true,
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Upload failed'),
      onFinish: () => setBusy(false),
    })
  }
  if (!patientId) return null
  return (
    <div className="flex items-center gap-2">
      <select value={folder} onChange={(e) => setFolder(e.target.value)}
        className="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700"
        title="Which folder of the patient file to put it in">
        {FOLDERS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <input ref={inputRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={onPick} />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
        className="inline-flex items-center gap-1.5 rounded-md bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-brand-primary/90 disabled:opacity-50"
        title="Upload a PDF or image straight into this patient's file">
        <Upload size={13} /> {busy ? 'Uploading…' : 'Upload file'}
      </button>
    </div>
  )
}

function HistoryTab({ appointments, conversations, communications = [], documents = [], writeSessions = [], chartedVisits = [], signedPack = null, intakeInProgress = null, patientId }) {
  return (
    <div className="space-y-5">
      {/* The signed intake pack — ONE card for the three forms of one sitting, the same card as on
          the Digital file. The two desk actions: confirm the ID was checked (by a named person) and
          record that the patient has their copy. */}
      {signedPack && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-base font-semibold text-brand-brown mb-1">Signed intake pack</h2>
          <p className="mb-3 text-xs text-gray-400">
            Signed electronically by the patient. When they arrive, check their ID and click
            <span className="font-semibold"> Confirm ID checked</span>; then give them their copy and record how.
          </p>
          <SignedPackCard pack={signedPack} />
        </div>
      )}

      {/* Patient file — scanned charts & estimates, filed automatically by the account code in the
          filename. One click opens the PDF inline in a new tab (Paul 2026-07-02). */}

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        {/* Upload straight into the file (Paul 2026-09-14: "There must be a file upload function here?").
            Before this, a document only arrived via the scanner, WhatsApp, an emailed letter or an intake form. */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base font-semibold text-brand-brown">Patient file — intake forms, scanned charts &amp; estimates</h2>
          <UploadPatientFile patientId={patientId} />
        </div>

        {/* An intake the patient STARTED on their phone but has not submitted. Paul 2026-09-16:
            "just show the form here even if not complete". Before this the panel said "No files yet"
            while a half-filled form sat in an IntakeDraft nobody could open — so the patient arrived
            and re-typed on paper what they had already typed. The pack prints pre-filled with whatever
            they got through; they check it, fill the gaps and sign at the desk. */}
        {intakeInProgress && (
          <div className="mb-4 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
            <Clock size={22} className="flex-shrink-0 text-amber-600" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-gray-800">
                Intake pack — {intakeInProgress.started ? 'started, not yet submitted' : 'sent, not started yet'}
              </p>
              <p className="truncate text-xs text-gray-500">
                {intakeInProgress.started
                  ? `Last saved ${intakeInProgress.updated_at}`
                  : `Sent ${intakeInProgress.sent_at || '—'}`}
                {' · '}
                {intakeInProgress.completed_count}/{intakeInProgress.total_count} forms done
                {intakeInProgress.expired ? ' · link expired' : ''}
                {' · '}
                {intakeInProgress.outstanding.map((f) => f.name).join(', ')} outstanding
              </p>
            </div>
            <div className="flex flex-shrink-0 items-center gap-1">
              <a href={intakeInProgress.pdf_url} target="_blank" rel="noopener noreferrer"
                title="Open the pack pre-filled with what they have completed so far — print it for them to check and sign"
                className="inline-flex items-center gap-1 rounded-md bg-brand-primary/10 px-2 py-1 text-xs font-semibold text-brand-primary hover:bg-brand-primary/20">
                <Printer size={13} /> View / print
              </a>
            </div>
          </div>
        )}

        {documents?.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {documents.map((doc) => (
              <div key={doc.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50">
                <FileText size={22} className="flex-shrink-0 text-red-600" />
                <div className="min-w-0 flex-1">
                  {/* Title first — a completed online intake pack, a scanned chart and a WhatsApp
                      document all landed here as a bare date otherwise (Paul 2026-08-13). */}
                  <p className="text-sm font-medium text-gray-800 truncate">{doc.title || doc.date || 'Undated'}</p>
                  <p className="text-xs text-gray-400 truncate">{doc.folder_label}{doc.date ? ` · ${doc.date}` : ''}</p>
                </div>
                <div className="flex flex-shrink-0 items-center gap-1">
                  <a href={doc.url || '#'} target="_blank" rel="noopener noreferrer" title="View — opens the PDF in a new tab"
                    className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold ${doc.url ? 'bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/20' : 'bg-gray-100 text-gray-400 pointer-events-none'}`}>
                    <Eye size={13} /> View
                  </a>
                  <a href={doc.url ? `${doc.url}?dl=1` : '#'} title="Download the PDF"
                    className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-semibold ${doc.url ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-gray-100 text-gray-400 pointer-events-none'}`}>
                    <Download size={13} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400">No files yet. Use <strong>Upload file</strong> above to file a PDF or image now. Completed online intake forms are filed here automatically, along with scanned patient-file pages (matched by the account code in the filename).</p>
        )}
      </div>

      {/* Shown when there is EITHER a hand-written session OR a digitally charted visit — gating
          it on pen sessions alone hid every on-screen chart from the one place staff look. */}
      {(writeSessions?.length > 0 || chartedVisits?.length > 0) && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-base font-semibold text-brand-brown mb-4">Charts &amp; treatment plans</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {/* Digitally charted visits sit in the SAME dated list as the hand-written ones, newest
                first — the chart done on screen must be as findable as the one drawn on paper. */}
            {chartedVisits.map((v) => (
              <a key={`cot-${v.id}`} href={`/courses-of-treatment/${v.id}`}
                className="flex items-center gap-3 rounded-lg border border-brand-border bg-white p-3 hover:bg-brand-primary/5">
                <ClipboardPlus size={22} className="flex-shrink-0 text-brand-primary" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-brand-ink">{v.at}</p>
                  <p className="truncate text-xs text-brand-muted">
                    {v.label} · {v.teeth} {v.teeth === 1 ? 'tooth' : 'teeth'} · {v.items} {v.items === 1 ? 'item' : 'items'}
                  </p>
                </div>
              </a>
            ))}
            {writeSessions.map((w) => (
              <a key={w.date} href={`/patients/${patientId}/write`}
                className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50 hover:bg-brand-primary/5">
                <FileText size={22} className="flex-shrink-0 text-brand-primary" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-800 truncate">{w.label}</p>
                  <p className="text-xs text-gray-400 truncate">Chart + treatment plan (hand-written)</p>
                </div>
                <span className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold bg-brand-primary/10 text-brand-primary">✍️ Open</span>
              </a>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-base font-semibold text-brand-brown mb-4">Appointment history</h2>
        {appointments?.length > 0 ? (
          <div className="space-y-2">
            {appointments.map((apt) => (
              <div key={apt.id} className="space-y-2">
              <Link href={`/appointments/${apt.id}`}
                className="block p-3 rounded-lg bg-gray-50 hover:bg-brand-cream transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      {new Date(apt.start_time).toLocaleDateString('en-ZA', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">{apt.reason || 'General'}</p>
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLES[apt.status] || 'bg-gray-100 text-gray-600'}`}>
                    {apt.status}
                  </span>
                </div>
                {apt.summary && (apt.summary.decisions || apt.summary.estimate_intent || (apt.summary.patient_questions || []).length > 0) && (
                  <div className="mt-3 pt-3 border-t border-gray-200/70 space-y-2">
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-brand-brown">✨ AI summary</span>
                    {apt.summary.decisions && (
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Decisions &amp; recommendations</p>
                        <p className="text-xs text-gray-700 whitespace-pre-wrap mt-0.5">{apt.summary.decisions}</p>
                      </div>
                    )}
                    {(apt.summary.patient_questions || []).length > 0 && (
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Patient Q&amp;A</p>
                        <ul className="space-y-1 mt-0.5">
                          {apt.summary.patient_questions.map((qa, i) => (
                            <li key={i} className="text-xs">
                              <span className="font-medium text-gray-800">Q: {qa.q}</span>
                              <span className="block text-gray-600">A: {qa.a}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {apt.summary.estimate_intent && (
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Estimate intent</p>
                        <p className="text-xs text-gray-700 whitespace-pre-wrap mt-0.5">{apt.summary.estimate_intent}</p>
                      </div>
                    )}
                  </div>
                )}
              </Link>

              {/* Safety-net proposal: audio auto-captured during this slot when Start Consultation
                  wasn't clicked. Owner-only (server-gated). "Add to file" attaches it; "Discard" rejects it. */}
              {apt.pending_scribe && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                    ⚠️ Unfiled chair-side recording
                  </p>
                  <p className="text-xs text-amber-800 mt-1">
                    Audio was captured during this appointment's time slot, but <b>Start Consultation wasn't
                    clicked</b>. Check it's this patient, then add it to the file.
                  </p>
                  <div className="mt-2 max-h-40 overflow-y-auto rounded bg-white/70 border border-amber-200 p-2 text-xs text-gray-700 whitespace-pre-wrap">
                    {apt.pending_scribe.preview}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      onClick={() => router.post(`/scribe-sessions/${apt.pending_scribe.id}/confirm`, {}, {
                        preserveScroll: true,
                        onSuccess: () => toast.success('Added to the patient’s file'),
                      })}
                      className="px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700">
                      Add to file
                    </button>
                    <button
                      onClick={() => { if (confirm('Discard this captured recording?')) router.post(`/scribe-sessions/${apt.pending_scribe.id}/discard`, {}, {
                        preserveScroll: true,
                        onSuccess: () => toast.success('Recording discarded'),
                      }) }}
                      className="px-3 py-1.5 rounded-md bg-white text-gray-600 text-xs font-medium border border-gray-300 hover:bg-gray-50">
                      Discard
                    </button>
                    <span className="text-[10px] text-amber-600 ml-auto">{apt.pending_scribe.chars} chars captured</span>
                  </div>
                </div>
              )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400">No appointments</p>
        )}
      </div>

      {/* The messages themselves, not a list of links to them. Reception's question is "what was said
          to this patient" — answering it used to mean clicking into the Conversations page, and after
          the 071 mirror the answer was split across two places entirely. */}
      <CommunicationsPanel communications={communications} />

      {/* The AI's own threads remain linkable for anyone who wants the live view with the composer.
          A mirror row is not listed: it has no composer and its content is already above. */}
      {conversations?.filter((c) => c.source !== 'mirror').length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-base font-semibold text-brand-brown mb-4">AI conversation threads</h2>
          <div className="space-y-2">
            {conversations.filter((c) => c.source !== 'mirror').map((conv) => (
              <Link key={conv.id} href={`/conversations/${conv.id}`}
                className="block p-3 rounded-lg bg-gray-50 hover:bg-brand-cream transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${conv.channel === 'whatsapp' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                      {conv.channel}
                    </span>
                    <span className="text-xs text-gray-400">{conv.message_count} messages</span>
                  </div>
                  <span className="text-xs text-gray-400">{dateZA(conv.updated_at)}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
      </div>
    </div>
  )
}

// ── Medical history panel ─────────────────────────────────────────
function MedicalHistoryPanel({ mh, scheme = null, onEdit }) {
  const isEmpty = !mh?.any_data

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <HeartPulse size={16} className="text-brand-taupe" />
          <h2 className="text-base font-semibold text-brand-brown">Medical History</h2>
        </div>
        <button type="button" onClick={onEdit}
          className="text-xs font-semibold text-brand-taupe hover:text-brand-brown transition-colors">
          {isEmpty ? '+ Add records' : 'Edit records'}
        </button>
      </div>

      {/* The structured medical aid + the MAIN MEMBER exactly as estimates/invoices print it — resolved live
          server-side by MedicalAidMainMember (mainmember-1015), never the raw snapshot column. */}
      {scheme && (
        <div className="mb-4 rounded-lg border border-gray-100 bg-gray-50/60 px-4 py-3" data-testid="medical-aid-main-member">
          <div className="flex items-center gap-1.5 mb-1">
            <Shield size={12} className="text-gray-400" />
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Medical aid</p>
          </div>
          <p className="text-sm text-gray-800">
            {scheme.scheme_name || '—'}{scheme.plan_option ? ` · ${scheme.plan_option}` : ''}
            {scheme.membership_number ? <span className="ml-1 font-mono text-xs text-gray-500">#{scheme.membership_number}</span> : null}
            {scheme.dependant_code ? <span className="ml-1 text-xs text-gray-500">· dep {scheme.dependant_code}</span> : null}
          </p>
          <p className="text-xs text-gray-600 mt-0.5">
            Main member: <strong>{scheme.main_member_name || '—'}</strong>
            {scheme.main_member_mode === 'self' ? ' (this patient)' : ''}
            {scheme.main_member_id_number ? <span className="ml-1 font-mono text-gray-500">ID {scheme.main_member_id_number}</span> : null}
          </p>
        </div>
      )}
      {isEmpty ? (
        <p className="text-sm text-gray-400">
          No medical records on file yet. Click "Add records" to capture allergies,
          medications, medical aid details, and emergency contact details.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <MhBlock label="Allergies" value={mh.allergies} tone="red" />
          <MhBlock label="Chronic conditions" value={mh.chronic_conditions} />
          <MhBlock label="Current medications" value={mh.current_medications} />
          <MhBlock label="Blood type" value={mh.blood_type} />
          <MhBlock label="Last dental visit" value={mh.last_dental_visit && dateZA(mh.last_dental_visit)} />
          <MhBlock label="Dental notes" value={mh.dental_notes} />
          <MhBlock label="Prosthesis / implants" value={mh.prosthesis} />
          <MhBlock label="Anaesthetic reaction" value={mh.anaesthesia_reaction} tone="red" />
          {mh.pregnant && <MhBlock label="Pregnancy" value="Yes" tone="red" />}

          {(mh.emergency_contact_name || mh.emergency_contact_phone) && (
            <div className="md:col-span-1">
              <div className="flex items-center gap-1.5 mb-1">
                <Phone size={12} className="text-gray-400" />
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Emergency contact</p>
              </div>
              <p className="text-sm text-gray-800">{mh.emergency_contact_name || '—'}</p>
              <p className="text-xs text-gray-500">{mh.emergency_contact_phone || '—'}</p>
            </div>
          )}

          {(mh.insurance_provider || mh.insurance_policy_number) && (
            <div className="md:col-span-1">
              <div className="flex items-center gap-1.5 mb-1">
                <Shield size={12} className="text-gray-400" />
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Medical Aid (claim-back)</p>
              </div>
              <p className="text-sm text-gray-800">{mh.insurance_provider || '—'}</p>
              <p className="text-xs text-gray-500 font-mono">{mh.insurance_policy_number || '—'}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function MhBlock({ label, value, tone }) {
  if (!value) return null
  const toneClass = tone === 'red' ? 'text-red-700' : 'text-gray-800'
  return (
    <div>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-sm ${toneClass} whitespace-pre-line`}>{value}</p>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</p>
      {children}
    </div>
  )
}
